import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import LegalBody from '../components/legal/LegalBody';
import { useAuth } from '../contexts/AuthContext';
import { roleLandingPath } from '../utils/appRoles';
import { legalApi, LegalDocument, LegalDocumentSummary, LegalScope } from '../services/api';
import { openCookiePreferences } from '../utils/cookieConsent';

/**
 * Hukuki metin sayfası.
 *
 * Rota bilerek GİRİŞ GEREKTİRMİYOR ve DashboardLayout'un dışında: aydınlatma
 * metni, kişi daha hesap açmadan önce okunabilir olmak zorunda (KVKK m.10 —
 * aydınlatma, veri işleme başlamadan yapılır). Ayrıca App Store ve Google Play,
 * gizlilik politikasının uygulama dışından açılabilen bir URL'de yayımlanmasını
 * şart koşuyor; mağaza formuna verilecek adres bu sayfadır.
 *
 * Gezinme listesi `GET /public/legal`'dan geliyor, burada sabit değil: belge
 * seti sunucuda büyüyünce (bryekosistem metinleri böyle eklendi) üç istemcinin
 * üçünü birden elle güncellemek gerekmesin.
 */
export const LEGAL_SLUGS = [
  'aydinlatma-metni',
  'cerez-politikasi',
  'cerez-listesi',
  'kvkk-politikasi',
  'bryekosistem-aydinlatma-metni',
  'bryekosistem-cerez-politikasi',
] as const;

export type LegalSlug = (typeof LEGAL_SLUGS)[number];

/**
 * Gezinme listesi sunucudan geliyor ama ROTALAR App.tsx'te elle yazılıyor.
 * Sunucuya 7. bir belge eklendiğinde bağlantısını çizmek, tıklanınca hiçbir
 * rotayla eşleşmeyip catch-all'a — yani sessizce dashboard'a — düşen bir
 * bağlantı üretirdi. Rotası olmayan slug listelenmez; belge eklenince
 * App.tsx'e rota eklemek zaten gerekiyor.
 */
function hasRoute(slug: string): boolean {
  return (LEGAL_SLUGS as readonly string[]).includes(slug);
}

/**
 * Sunucudan liste gelmezse kullanılan yedek. Öncesinde catch bloğu boştu ve
 * gezinme kartı sessizce bomboş bir kutuya dönüşüyordu — açık olan belge
 * dışında hiçbir belgeye geçilemiyordu ve kullanıcıya bunun sebebi
 * söylenmiyordu.
 */
const FALLBACK_INDEX: LegalDocumentSummary[] = [
  { slug: 'aydinlatma-metni', title: '', short_title: 'Aydınlatma Metni', scope: 'lms', version: '', effective_date: '' },
  { slug: 'cerez-politikasi', title: '', short_title: 'Çerez Politikası', scope: 'lms', version: '', effective_date: '' },
  { slug: 'cerez-listesi', title: '', short_title: 'Çerez Listesi', scope: 'lms', version: '', effective_date: '' },
  { slug: 'kvkk-politikasi', title: '', short_title: 'KVKK Politikası', scope: 'kurumsal', version: '', effective_date: '' },
  { slug: 'bryekosistem-aydinlatma-metni', title: '', short_title: 'bryekosistem İnternet Sitesi Aydınlatma Metni', scope: 'kurumsal', version: '', effective_date: '' },
  { slug: 'bryekosistem-cerez-politikasi', title: '', short_title: 'bryekosistem Çerez Politikası', scope: 'kurumsal', version: '', effective_date: '' },
];

/**
 * Kapsam başlıkları. Altı belgeyi tek düz liste hâlinde göstermek "hangisi beni
 * bağlıyor?" sorusunu cevapsız bırakıyordu; açıklama satırı bunu kapatıyor.
 */
const SCOPES: { id: LegalScope; label: string; note: string }[] = [
  {
    id: 'lms',
    label: 'bryekosis LMS',
    note: 'Bu uygulamayı kullanırken geçerli olan metinler.',
  },
  {
    id: 'kurumsal',
    label: 'BRY Birey ve bryekosistem',
    note:
      'Giriş işlemleriniz Birey Kimlik üzerinden yürüdüğü için bu metinler de sizi ilgilendirir.',
  },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

const LegalPage: React.FC<{ slug: LegalSlug }> = ({ slug }) => {
  const { user } = useAuth();

  /**
   * Uygulamaya dönüş hedefi — bilerek `navigate(-1)` DEĞİL.
   *
   * Geçmişte bir adım geri gitmek yalnızca kullanıcı buraya uygulama içinden
   * geldiyse çalışır. Bu sayfaların adresleri mağaza kayıtlarında, e-postada
   * ve giriş ekranında paylaşılıyor; doğrudan açan kişide geri gidilecek bir
   * geçmiş yok ve düğme sekmeyi terk ediyor ya da hiçbir şey yapmıyordu.
   * Gerçek bir hedefe gitmek her iki durumda da doğru sonucu veriyor.
   */
  const homePath = user ? roleLandingPath(user.role) : '/login';
  const homeLabel = user ? 'Uygulamaya dön' : 'Giriş ekranına dön';
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [index, setIndex] = useState<LegalDocumentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    legalApi
      .document(slug)
      .then((res) => {
        if (!cancelled) setDoc(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('Belge şu anda görüntülenemiyor. Lütfen daha sonra tekrar deneyin.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Gezinme listesi belgeden bağımsız — slug değişince yeniden çekilmesin.
  useEffect(() => {
    let cancelled = false;
    legalApi
      .list()
      .then((res) => {
        if (!cancelled) setIndex(res.data.documents.filter((d) => hasRoute(d.slug)));
      })
      .catch(() => {
        if (!cancelled) setIndex(FALLBACK_INDEX);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (doc) document.title = `${doc.short_title} — bryekosis LMS`;
    return () => {
      document.title = 'bryekosis LMS';
    };
  }, [doc]);

  return (
    <div className="min-h-screen bg-[#f4f6fa]">
      <header className="border-b border-[#e6e9ef] bg-white">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between gap-4 px-5 py-4">
          <Link to={homePath} className="shrink-0" aria-label={homeLabel}>
            <BrandLogo variant="mark" className="h-[34px] object-contain" />
          </Link>
          <Link
            to={homePath}
            className="mr-auto ml-1 hidden items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[13px] font-semibold text-[#3973bf] hover:bg-[#eaf1fb] sm:inline-flex"
          >
            <span aria-hidden>←</span> {homeLabel}
          </Link>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="text-[13px] font-semibold text-[#3973bf] hover:underline"
          >
            Çerez Tercihleri
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-5 py-8 lg:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
          {/* Belge listesi — mobilde içeriğin üstünde, masaüstünde sabit sütun.
              Uzun bir hukuki metnin altına gömülü bir liste, belgeler arasında
              geçmek için her seferinde sonuna kadar kaydırmak demekti. */}
          <nav
            aria-label="Yasal belgeler"
            className="shrink-0 rounded-[18px] border border-[#e6e9ef] bg-white p-4 lg:sticky lg:top-6 lg:w-[286px]"
          >
            {SCOPES.map((scope) => {
              const docs = index.filter((d) => d.scope === scope.id);
              if (docs.length === 0) return null;
              return (
                <div key={scope.id} className="mb-4 last:mb-0">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a93a2]">
                    {scope.label}
                  </p>
                  <p className="mt-1 mb-2 text-[11.5px] leading-[1.5] text-[#a3abb8]">{scope.note}</p>
                  <ul className="space-y-0.5">
                    {docs.map((d) => (
                      <li key={d.slug}>
                        <Link
                          to={`/${d.slug}`}
                          aria-current={d.slug === slug ? 'page' : undefined}
                          className={`block rounded-[10px] px-2.5 py-2 text-[13px] leading-snug transition-colors ${
                            d.slug === slug
                              ? 'bg-[#eaf1fb] font-semibold text-[#3973bf]'
                              : 'text-[#4a5260] hover:bg-[#f4f6fa]'
                          }`}
                        >
                          {d.short_title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>

          <div className="min-w-0 flex-1">
            <div className="rounded-[20px] border border-[#e6e9ef] bg-white p-6 shadow-[0_10px_40px_rgba(20,30,50,0.05)] lg:p-10">
              {error && <p className="text-[14px] text-[#c2410c]">{error}</p>}

              {!error && !doc && <p className="text-[14px] text-[#8a93a2]">Yükleniyor…</p>}

              {doc && (
                <>
                  <h1 className="text-[20px] font-bold leading-snug text-[#2b2b2b] lg:text-[24px]">
                    {doc.title}
                  </h1>
                  <p className="mt-2 mb-8 text-[12.5px] text-[#8a93a2]">
                    Yürürlük tarihi: {formatDate(doc.effective_date)} · Sürüm {doc.version}
                  </p>
                  <LegalBody body={doc.body} />
                </>
              )}
            </div>

            <div className="mt-6 text-center">
              <Link to={homePath} className="text-[13px] font-semibold text-[#3973bf] hover:underline">
                ← {homeLabel}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
